import { BProgress } from "@bprogress/core";
import {
  createRouter,
  createWebHistory,
  START_LOCATION,
  type RouteLocationNormalized,
} from "vue-router";

import routes from "./routes";
import { validateRouteAccess } from "./utilities";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

/**
 * Track the previous route (useful in some odd cases)
 *
 * For example, can be used to prevent "duplicate" routing history when navigating "back" to another page.
 *   If the target page was the previous page, `router.back` can be used; otherwise, another navigation method.
 */
export let previousRoute: RouteLocationNormalized | null = null;

/** Delay before showing route loading progress bar after navigation event (in ms) */
const bProgressDelay = 250;
let bProgressDelayTimer: ReturnType<typeof setTimeout> | null = null;

// Routing guards are primarily configured for protecting routes based on authentication
// NOTE: Global 'beforeEach' guard will be executed almost immediately in navigation event hierarchy, and
//         can be used to ensure that required data has been loaded for other guards (global or component)
//         before continuing with navigation (allowing other guards to have required data available).
router.beforeEach((to, from, next) => {
  // Only show route loading progress bar for lengthier navigation events (ie. loading lazy routes, etc)
  // NOTE: Must clear previous delay time to prevent permanently showing progress with rapid consecutive navigation
  if (bProgressDelayTimer) {
    clearTimeout(bProgressDelayTimer);
  }
  bProgressDelayTimer = setTimeout(() => {
    BProgress.start();
  }, bProgressDelay);

  // Route validation may be "skipped" if root authentication check is still in progress, as it will
  //   instead be handled by the component performing the authentication check.
  const routeValidation = validateRouteAccess(to);

  // Route navigation is either redirected or cancelled if target route validation fails
  if (!routeValidation.valid) {
    // If routing has not occurred yet (ie. cannot cancel navigation) and there is no redirect, go to root route
    if (from === START_LOCATION && !routeValidation.redirect) {
      return next("/");
    }
    return routeValidation.redirect ? next(routeValidation.redirect) : next(false);
  }

  return next();
});

router.afterEach((to, from) => {
  // Cleanup delay timer to prevent showing progress bar if navigation is quick
  if (bProgressDelayTimer) {
    clearTimeout(bProgressDelayTimer);
  }
  if (BProgress.isStarted()) {
    BProgress.done();
  }

  // NOTE: Handled in `afterEach` guard to ensure previous route is only updated if route actually changed
  //         (ie. navigation was not cancelled).
  previousRoute = from;
});

export { router };
